// Sélecteur rapide de dossier : dropdown ancré sous le titre du dossier.
// Permet de switcher de dossier en restant sur l'onglet courant.
// Le contenu du panneau vit dans DossierPickerPanel (partagé avec la vue Tâches).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useDossiers, type Dossier } from '../../hooks/queries/useDossiers';
import { DossierPickerPanel } from './DossierPickerPanel';

interface Props {
  currentDossier: Dossier;
  currentTab: string;
}

export function DossierSwitcher({ currentDossier, currentTab }: Props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Liste de résolution id→dossier (RLS respectée, déjà en cache ailleurs).
  const { data: allDossiers } = useDossiers({});

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function go(id: string) {
    navigate(`/dossiers/${id}/${currentTab}`);
    setOpen(false);
  }

  return (
    <div className="relative">
      {/* Déclencheur : titre cliquable */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="group flex items-center gap-1.5 text-left max-w-full"
      >
        <span
          className="truncate text-[21px] font-semibold leading-tight text-cime-text-primary"
          style={{ fontFamily: 'var(--cime-font-heading)' }}
        >
          {currentDossier.title}
        </span>
        <ChevronDown className="w-4 h-4 shrink-0 text-cime-text-muted group-hover:text-cime-primary" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 w-80 bg-white border border-neutral-200 rounded-cime shadow-lg overflow-hidden">
            <DossierPickerPanel
              dossiers={allDossiers ?? []}
              excludeId={currentDossier.id}
              onPick={go}
            />
          </div>
        </>
      )}
    </div>
  );
}
